import { Box, Card, CardContent, Divider, Typography, useMediaQuery, useTheme } from '@mui/material';
import { LoanData } from 'TYPES/api.body';
import { RightArrowIcon } from 'assets/Icons';
import renderIcon from 'icon';
import QUERY_KEYS from 'network/config/queryKeys';
import { getAllLoans } from 'network/services/loanManagement';
import { useState } from 'react';
import { IoArrowBackCircleSharp } from 'react-icons/io5';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from 'utils/currencyFormater';
import { formatDate } from 'utils/dateFormater';
import {
  BNPLCon,
  ContentCon,
  IconBtn,
  RowStatus,
  Table,
  TableBody,
  TableData,
  TableHead,
  TableRow,
  TableTab,
  TableTabs
} from './styles';

const tabs = [
  { label: 'Requests', value: 'requests' },
  { label: 'Approved', value: 'approved' },
  { label: 'Paid', value: 'paid' }
];

export const BuyNowPayLater = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [tabValue, setTabValue] = useState('requests');
  const [loanDisplayData, setLoanDisplayData] = useState<LoanData[]>([]);
  const { data, isLoading } = useQuery({
    queryFn: getAllLoans,
    queryKey: [QUERY_KEYS.ALL_LOANS],
    onSuccess: data => {
      setLoanDisplayData(data.data ?? []);
    }
  });

  const handleTabChange = (newValue: string) => {
    setTabValue(newValue);

    if (newValue === 'approved' || newValue === 'paid') {
      const filteredLoans = (data?.data ?? []).filter(loan => loan.status === newValue);
      return setLoanDisplayData(filteredLoans);
    }

    setLoanDisplayData(data?.data ?? []);
  };

  const handleDetails = (id: number, data: LoanData) => {
    navigate(`/account/bnpl/${id}`, { state: data });
  };

  const MobileCard = ({ loan }: { loan: LoanData }) => (
    <Card
      sx={{
        mb: 2,
        cursor: 'pointer',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 3
        }
      }}
      onClick={() => handleDetails(loan.id, loan)}>
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">
            BNPL Amount
          </Typography>
          <Typography variant="body2" fontWeight={500}>
            {formatCurrency(loan?.loanAmount)}
          </Typography>
        </Box>

        <Divider sx={{ my: 1 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Payback Amount
          </Typography>
          <Typography variant="body2" fontWeight={500}>
            {formatCurrency(loan?.totalAmountWithInterest)}
          </Typography>
        </Box>

        <Divider sx={{ my: 1 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Date of Request
          </Typography>
          <Typography variant="body2">{formatDate(loan?.dateRequested)}</Typography>
        </Box>

        <Divider sx={{ my: 1 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Payback Date
          </Typography>
          <Typography variant="body2">{formatDate(loan?.payBackDate)}</Typography>
        </Box>

        <Divider sx={{ my: 1 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle2" color="text.secondary">
            Status
          </Typography>
          <RowStatus className={`status-${loan?.status.toLowerCase()}`}>
            <p
              className={`py-1 px-2 rounded-md ${
                loan.status === 'CANCELLED' ? 'bg-red-200 text-red-600' : 'bg-yellow-200 text-yellow-700'
              }`}>
              {loan.status}
            </p>
          </RowStatus>
        </Box>

        <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
          <RightArrowIcon style={{ width: 20, height: 20, color: '#6b7280' }} />
        </Box>
      </CardContent>
    </Card>
  );

  const emptyStateMessage = isLoading
    ? 'Loading...'
    : `You don't have any ${
        tabValue === 'approved' ? 'approved' : tabValue === 'paid' ? 'paid' : ''
      } loans at the moment.`;

  return (
    <>
      <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
        {renderIcon(IoArrowBackCircleSharp, {
          size: 24,
          className: 'text-black flex-shrink-0 cursor-pointer hover:text-gray-600 transition-colors',
          onClick: () => navigate('/account')
        })}
        <span
          className="font-semibold"
          style={{ color: '#222', cursor: 'pointer' }}
          onClick={() => navigate('/account')}>
          Account
        </span>
        <span>›</span>
        <span className="font-semibold" style={{ color: '#222' }}>
          BNPL
        </span>
      </div>
      <BNPLCon>
        <div className="header">BNPL Loans</div>

        <ContentCon>
          <TableTabs spacing={0} direction="row">
            {tabs.map(tab => (
              <TableTab
                key={tab.label}
                className={tabValue === tab.value ? 'active' : ''}
                onClick={() => handleTabChange(tab.value)}>
                {tab.label}
              </TableTab>
            ))}
          </TableTabs>

          {/* Desktop Table View */}
          {!isMobile && (
            <Table>
              <TableHead>
                <TableRow>
                  <TableData>BNPL Amount</TableData>
                  <TableData>Payback Amount</TableData>
                  <TableData>Date of Request</TableData>
                  <TableData>Payback Date</TableData>
                  <TableData>Status</TableData>
                  <TableData></TableData>
                </TableRow>
              </TableHead>
              <TableBody>
                {loanDisplayData.length < 1 ? (
                  <TableRow>
                    <TableData colSpan={6} className="empty-state">
                      {emptyStateMessage}
                    </TableData>
                  </TableRow>
                ) : (
                  loanDisplayData.map(loan => {
                    return (
                      <TableRow key={loan.id}>
                        <TableData>{formatCurrency(loan?.loanAmount)}</TableData>
                        <TableData>{formatCurrency(loan?.totalAmountWithInterest)}</TableData>
                        <TableData>{formatDate(loan?.dateRequested)}</TableData>
                        <TableData>{formatDate(loan?.payBackDate)}</TableData>
                        <TableData>
                          <RowStatus className={`status-${loan?.status.toLowerCase()}`}>
                            <p
                              className={`py-1 px-2 rounded-md ${
                                loan.status === 'CANCELLED'
                                  ? 'bg-red-200 text-red-600'
                                  : 'bg-yellow-200 text-yellow-700'
                              }`}>
                              {loan.status}
                            </p>
                          </RowStatus>
                        </TableData>
                        <TableData>
                          <IconBtn onClick={() => handleDetails(loan?.id, loan)}>
                            <RightArrowIcon className="icon" />
                          </IconBtn>
                        </TableData>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          )}

          {/* Mobile Card View */}
          {isMobile && (
            <Box sx={{ p: 2 }}>
              {loanDisplayData.length < 1 ? (
                <Box sx={{ textAlign: 'center', py: 5 }}>
                  <Typography variant="body1" color="text.secondary">
                    {emptyStateMessage}
                  </Typography>
                </Box>
              ) : (
                loanDisplayData.map(loan => <MobileCard key={loan.id} loan={loan} />)
              )}
            </Box>
          )}
        </ContentCon>
      </BNPLCon>
    </>
  );
};
